import { useEffect, useRef } from "react";
import { Select } from "antd";
import type { SelectProps } from "antd";

type SiteSelectProps = SelectProps<number> & {
  /**
   * Set to false where a blank value is a real answer rather than an unfilled one - the
   * tool and stock-location forms read "no site" as Central, so the lone site there is
   * still a choice the user has to make.
   */
  autoSelectSingle?: boolean;
};

/**
 * The site picker. Most people work under one site - their grants reach no further, so
 * the list arrives holding a single entry - and a one-option dropdown is a keystroke, not
 * a decision: it fills itself in. Drop-in for the plain Select, inside a Form.Item (which
 * supplies value and onChange, and keeps any onChange the caller passed) or controlled by
 * the page's own state.
 */
export function SiteSelect({ options = [], value, onChange, autoSelectSingle = true, ...rest }: SiteSelectProps) {
  // The site already filled in, so clearing a clearable picker stays cleared rather than
  // being answered again on the next render. A different site arriving - the project
  // above the picker changed, say - is a new question, and gets filled in afresh.
  const filledWith = useRef<unknown>(undefined);
  const only = options.length === 1 ? options[0] : undefined;

  useEffect(() => {
    if (!autoSelectSingle || !only || value != null || filledWith.current === only.value) return;
    filledWith.current = only.value;
    onChange?.(only.value as number, only);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectSingle, value, only?.value]);

  return <Select {...rest} options={options} value={value} onChange={onChange} />;
}
